import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from 'oidc-react';
import { useCreateGroup } from '../../providers/CreateGroupProvider';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useTenantGroupService } from '../../components/services/usePolicyAutomationService';
import { usePermissions } from '../../hooks/usePermissions';

const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const daysMap: { [key: string]: string } = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};

const RebootTable: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { CreateGroupData, setCreateGroupData, resetCreateGroupData } = useCreateGroup();
  const { selectedTenantId } = useTenantSelection();
  const { createOrUpdateTenantGroup } = useTenantGroupService();
  const { canEdit } = usePermissions();
  const readOnly = !canEdit('groupPolicies');
  const validationSchema = Yup.object().shape({
    delayRebootByUnit: Yup.string().required('Reboot Delay Unit is required'),

    delayRebootValue: Yup.mixed()
      .required('Delay Reboot Value is required')
      .when('delayRebootByUnit', {
        is: 'Hours',
        then: (schema) =>
          Yup.number()
            .typeError('Delay Reboot Value must be a number')
            .min(1, 'Min 1 hour')
            .max(72, 'Max 72 hours')
            .required('Delay Reboot Value is required'),
      })
      .when('delayRebootByUnit', {
        is: 'Days',
        then: (schema) => Yup.string().required('Delay Reboot Value is required'),
      }),
  });
  const dayOptions = [1, 2, 3];
  const handleCreateGroup = async (values: any) => {
    try {
      if (!auth?.userData) {
        console.error('Not authenticated');
        return;
      }

      const token = auth.userData.access_token;
      const tenantId = selectedTenantId;

      await createOrUpdateTenantGroup(token, tenantId as string, CreateGroupData, values);

      resetCreateGroupData();
      navigate('/Group');
    } catch (err) {
      console.error('❌ create-group failed:', err);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        startTime: CreateGroupData?.rebootSchedule?.startTime || '',
        endTime: CreateGroupData?.rebootSchedule?.endTime || '',
        selectedDays: CreateGroupData?.rebootSchedule?.selectedDays || [],
        delayRebootByUnit: CreateGroupData?.groupAgentRebootDelayConfiguration?.delayRebootByUnit || '',
        delayRebootValue: CreateGroupData?.groupAgentRebootDelayConfiguration?.delayRebootValue || '',
      }}
      validationSchema={readOnly ? undefined : validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        if (readOnly) {
          navigate('/Group');
          return;
        }

        try {
          // Save form state to context before calling API
          setCreateGroupData((prev: any) => ({
            ...prev,
            groupMaintenanceSchedule: [
              {
                ...prev.groupMaintenanceSchedule[0],
                frequency: 'Days',
                interval: 1,
              },
            ],
          }));

          // Then call API
          await handleCreateGroup(values);
        } catch (err) {
          console.error(err);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        const getTimeOptions = () => {
          const max = values.delayRebootByUnit === 'Hours' ? 24 : values.delayRebootByUnit === 'Days' ? 1 : 3;
          return Array.from({ length: max }, (_, i) => i + 1);
        };

        return (
          <Form className="bg-[var(--Background-Color,#1D1D1D)] text-white p-6 rounded-lg space-y-6 flex-1">
            {/* Time Inputs */}
            {/* <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm mb-1">Start Time*</label>
                <Field type="time" name="startTime" className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600" />
              </div>
              <div className="flex-1">
                <label className="block text-sm mb-1">End Time*</label>
                <Field type="time" name="endTime" className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600" />
              </div>
            </div> */}

            {/* Days Selection */}
            {/* <div>
              <label className="block text-sm mb-1">Select Days*</label>
              <div className="flex space-x-2 mb-1 flex-wrap">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const isSelected = values.selectedDays.includes(day);
                      const newDays = isSelected ? values.selectedDays.filter((d) => d !== day) : [...values.selectedDays, day];
                      setFieldValue('selectedDays', newDays);
                    }}
                    className={`px-4 py-2 rounded mb-2 ${values.selectedDays.includes(day) ? 'bg-red-600' : 'bg-[#1e1e1e]'}`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              {values.selectedDays.length > 0 && (
                <p className="text-xs text-gray-400">Occurs every {values.selectedDays.map((d) => daysMap[d]).join(', ')}</p>
              )}
            </div> */}

            {/* Delay Configuration */}
            <div className="flex space-x-4">
              {/* Reboot Delay Unit */}
              <div className="flex-1">
                <label className="block text-sm mb-1">Reboot Delay Unit*</label>
                <Field
                  as="select"
                  name="delayRebootByUnit"
                  disabled={readOnly}
                  className="w-full bg-[#1e1e1e] text-white p-2 rounded border border-gray-600"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setFieldValue('delayRebootByUnit', e.target.value);
                    setFieldValue('delayRebootValue', '');
                  }}
                >
                  <option value="">Select Unit</option>
                  <option value="Hours">Hours</option>
                  <option value="Days">Days</option>
                </Field>
                <ErrorMessage name="delayRebootByUnit" component="div" className="text-red-400 text-xs mt-1" />
              </div>

              {/* Delay Reboot Value */}
              <div className="flex-1">
                <label className="block text-sm mb-1">Reboot Delay Value*</label>
                <div className="w-full">
                  {values.delayRebootByUnit === 'Hours' ? (
                    <Field
                      type="number"
                      name="delayRebootValue"
                      min={1}
                      max={72}
                      placeholder="Max 72 hours"
                      disabled={readOnly || !values.delayRebootByUnit || values.delayRebootByUnit === ''}
                      className="w-full h-10 bg-[#1e1e1e] text-white px-2 rounded border border-gray-600 disabled:opacity-50 appearance-none"
                    />
                  ) : (
                    <Field
                      as="select"
                      name="delayRebootValue"
                      disabled={readOnly || !values.delayRebootByUnit || values.delayRebootByUnit === ''}
                      className="w-full h-10 bg-[#1e1e1e] text-white px-2 rounded border border-gray-600 disabled:opacity-50 appearance-none"
                    >
                      <option value="">Select Value</option>
                      {values.delayRebootByUnit === 'Days'
                        ? [1, 2, 3].map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))
                        : null}
                    </Field>
                  )}
                </div>
                <ErrorMessage name="delayRebootValue" component="div" className="text-red-400 text-xs mt-1" />
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="button" style={closeBtnStyle} onClick={() => navigate('/Group')}>
                Discard
              </button>

              <button
                type="button"
                style={closeBtnStyle}
                onClick={() => {
                  // Save form data before going to previous page
                  setCreateGroupData((prev: any) => ({
                    ...prev,
                    groupMaintenanceSchedule: [
                      {
                        ...prev.groupMaintenanceSchedule[0],
                        // timeWindowStart: values.startTime,
                        // timeWindowEnd: values.endTime,
                        // daysOfWeek: values.selectedDays.map((day: string) => daysMap[day]), // ✅ full names for API
                        frequency: 'Days',
                        interval: 1,
                      },
                    ],
                  }));

                  navigate('/groupPolicy/MaintananceGroup');
                }}
              >
                Previous
              </button>

              <button type="submit" style={nextBtnStyle} disabled={isSubmitting}>
                {readOnly
                  ? 'Done'
                  : isSubmitting
                    ? CreateGroupData.TenantGroupId
                      ? 'Updating...'
                      : 'Creating...'
                    : CreateGroupData.TenantGroupId
                      ? 'Update'
                      : 'Create'}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const closeBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #555',
  borderRadius: '2rem',
  cursor: 'pointer',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

const nextBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.5rem',
  backgroundColor: '#b31317',
  color: '#fff',
  border: 'none',
  borderRadius: '2rem',
  cursor: 'pointer',
  fontWeight: 'bold',
  boxShadow: '2px 2px 6px rgba(0,0,0,0.4)',
};

export default RebootTable;
